import type { StaticImageData } from 'next/image';
import { rewriteStaticAssetUrl } from '@/lib/static-asset-url';

export type ProductImageSrc = string | StaticImageData;

function isStaticImageData(value: unknown): value is StaticImageData {
  return (
    typeof value === 'object' &&
    value !== null &&
    'src' in value &&
    typeof (value as StaticImageData).src === 'string'
  );
}

/**
 * Normalizes product image `src` values from APIs (missing, empty, or non-string).
 */
export function resolveProductImageSrc(
  src: unknown,
  fallback: ProductImageSrc,
): ProductImageSrc {
  if (src == null) return fallback;
  if (isStaticImageData(src)) return src;
  if (typeof src === 'string') {
    const trimmed = src.trim();
    return trimmed || fallback;
  }
  return fallback;
}

/** URL string for raw `<img>` tags (zoom overlay, etc.). */
export function getDisplayImageUrl(src: ProductImageSrc): string {
  if (isStaticImageData(src)) return src.src;
  return rewriteStaticAssetUrl(src);
}

/**
 * Remote hostnames allowed for `next/image` optimization.
 * Keep in sync with `next.config.js` → `images.domains`.
 */
const NEXT_IMAGE_OPTIMIZABLE_HOSTS = new Set<string>([
  'pickbazarlaravel.s3.ap-southeast-1.amazonaws.com',
  'pixarlaravel.s3.ap-southeast-1.amazonaws.com',
  'lh3.googleusercontent.com',
  'localhost',
  '127.0.0.1',
  'i.pravatar.cc',
  'p7483342c1dev-store.occa.ocs.oraclecloud.com',
  'p7483342c1prd-store.occa.ocs.oraclecloud.com',
  'textract-console-us-east-2-f46a84ab-e10d-453b-baf5-b6f89042fd3e.s3.us-east-2.amazonaws.com',
  'textract-console-us-east-2-f46a84ab-e10d-453b-baf5-b6f89042fd3e.s3.amazonaws.com',
  'qualidoc.com.br',
  'www.qualidoc.com.br',
  'qualidoc-plataforma.s3.us-east-2.amazonaws.com',
  'www.qualidoc.life',
  'hom.qualidoc.life',
]);

/**
 * Returns true when Next.js can safely optimize this `src` (remotePatterns / domains).
 * Local paths, static imports, and known CDN hosts → optimized; unknown remotes → unoptimized (no runtime error).
 *
 * **Development:** always returns `false`. Next 13.x + newer Node (e.g. v24) can surface
 * image-optimizer failures as errors containing circular `ClientRequest` refs; Next’s
 * dev worker then crashes when serializing them (`Converting circular structure to JSON`).
 * Production builds still use the optimizer for allowlisted hosts.
 */
export function shouldOptimizeNextImage(
  src: string | StaticImageData | undefined | null,
): boolean {
  if (process.env.NODE_ENV !== 'production') {
    return false;
  }
  if (src == null) return false;
  if (typeof src === 'object' && 'src' in src) {
    const path = (src as StaticImageData).src;
    if (typeof path === 'string' && /\.svg(\?|$)/i.test(path)) {
      return false;
    }
    return true;
  }
  const s = String(src).trim();
  if (!s) return false;
  if (s.startsWith('/') && !s.startsWith('//')) {
    return true;
  }
  try {
    const { hostname } = new URL(s);
    return NEXT_IMAGE_OPTIMIZABLE_HOSTS.has(hostname);
  } catch {
    return false;
  }
}
